import { useEffect, useRef, useState } from 'react';
import { useUI } from 'contexts/UIContext';
import BuilderLineGroups from 'components/cart/builderLineGroups';
import SlidePanel from '../slidePanel/slidePanel';
import CartHeader from '../cartHeader/cartHeader';
import CartLineItem from '../cartLineItem/cartLineItem';
import CtaSlot from '../ctaSlot/ctaSlot';
import CartFooter from '../cartFooter/cartFooter';
import SalonSelector from '../salonSelector/salonSelector';
import SalonPanel from '../panels/salonPanel/salonPanel';
import GiftChoicePanel from '../panels/giftChoicePanel/giftChoicePanel';
import useCartDrawerData from '../useCartDrawerData';
import { buildViewCartEcommerce } from './buildViewCartEcommerce';
import styles from './cartDrawer.module.scss';

const CartDrawer = ({ locale }) => {
  const { displayCartPopup, closeCartPopup } = useUI();
  const data = useCartDrawerData(locale);
  const [activePanel, setActivePanel] = useState(null);
  const giftAutoOpenedCountRef = useRef(0);
  const viewCartFiredRef = useRef(false);

  useEffect(() => {
    const scrollWidth = window.innerWidth - document.documentElement.clientWidth;
    if (displayCartPopup) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = `0px`;
      setActivePanel(null);
      viewCartFiredRef.current = false;
    }
  }, [displayCartPopup]);

  const multiChoiceCount = data.choiceGroups.filter(g => g.freeProducts.length > 1).length;

  useEffect(() => {
    if (!displayCartPopup) return;
    if (multiChoiceCount > giftAutoOpenedCountRef.current) setActivePanel('gift');
    giftAutoOpenedCountRef.current = multiChoiceCount;
  }, [displayCartPopup, multiChoiceCount]);

  useEffect(() => {
    if (!displayCartPopup || viewCartFiredRef.current) return;
    const lineItems = data.cart?.lines?.edges || [];
    // Fire once Contentful has settled (ok or failed) — categories are best-effort
    // enrichment, not a gate, so analytics still come through if the fetch fails.
    if (!lineItems.length || !data.contentfulLoaded || typeof window === 'undefined') return;

    const ecommerce = buildViewCartEcommerce({
      currencyCode: data.cart?.cost?.totalAmount?.currencyCode,
      lineItems,
      contentfulData: Object.values(data.contentfulData),
      trackedItemsSelectList: data.trackedItemsSelectList,
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // clear the previous ecommerce
    window.dataLayer.push({ event: 'view_cart', ecommerce });
    viewCartFiredRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayCartPopup, data.contentfulLoaded, data.cart?.lines?.edges]);

  return (
    <SlidePanel
      isOpen={displayCartPopup}
      onClose={closeCartPopup}
      ariaLabel={data.translations?.accessibility?.cartDialog || 'Cart'}
    >
      <div className={styles.content}>
        <CartHeader data={data} onClose={closeCartPopup} />
        {data.isEmpty && (
          <p className={styles.emptyMessage}>{data.translations?.cart?.emptyCart}</p>
        )}
        <BuilderLineGroups
          lines={data.displayLines}
          idPrefix="builder-group"
          renderLine={(line, index) => (
            <CartLineItem
              locale={locale}
              lineItem={line.node}
              key={line.node.id}
              index={index}
              cart={data.cart}
              contentfulData={data.contentfulData[line.node?.merchandise?.product?.handle]}
            />
          )}
        />
        <div className={styles.tailSpacer} />
        {data.giftUnavailable &&
        typeof data.translations?.freeItem?.outOfStockMessage === 'string' ? (
          <p className={styles.giftNotice} role="status">
            {data.translations.freeItem.outOfStockMessage}
          </p>
        ) : null}
        <CtaSlot
          isEmpty={data.isEmpty}
          giftChoiceAvailable={data.choiceGroups.some(g => !g.selected)}
          onOpenGiftChoice={() => setActivePanel('gift')}
          upsellItems={data.upsellItems}
          translations={data.translations}
          links={data.links}
          locale={locale}
          onClose={closeCartPopup}
        />
        <SalonSelector
          data={data}
          locale={locale}
          onOpenSalonPanel={() => setActivePanel('salon')}
        />
      </div>
      <CartFooter data={data} locale={locale} onClose={closeCartPopup} />
      <SalonPanel
        isOpen={activePanel === 'salon'}
        onClose={() => setActivePanel(null)}
        onCloseDrawer={closeCartPopup}
        locale={locale}
      />
      <GiftChoicePanel
        isOpen={activePanel === 'gift'}
        onClose={() => setActivePanel(null)}
        onCloseDrawer={closeCartPopup}
        locale={locale}
        cart={data.cart}
        choiceGroups={data.choiceGroups}
        selectGift={data.selectGift}
      />
    </SlidePanel>
  );
};

export default CartDrawer;
